/**
 * Permit-to-Work entity DTOs.
 *
 * Mirrors Prisma `PermitToWork` (prisma/schema.prisma:988). Revocation is a
 * dedicated POST endpoint (sets `isRevoked` + `revokedAt`) — the
 * PermitUpdateRequest schema is for editing pre-revocation fields only.
 *
 * Ids are cuid() per the Prisma model.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";
import { CursorPaginatedResponse } from "../pagination";

export const PERMIT_TYPES = [
  "HOT_WORKS",
  "WORKING_AT_HEIGHT",
  "CONFINED_SPACE",
  "ELECTRICAL_ISOLATION",
  "EXCAVATION",
  "GENERAL",
] as const;

export type PermitType = (typeof PERMIT_TYPES)[number];

export const PermitTypeSchema = z.enum(PERMIT_TYPES);

export const PermitSchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    // Recorder provenance, not ownership — null once the author deleted their
    // account (PermitToWork.userId is SetNull so evidence outlives authors).
    userId: z.string().nullable(),
    permitType: PermitTypeSchema,
    location: z.string(),
    description: z.string(),
    workStartsAt: z.string().datetime(),
    workEndsAt: z.string().datetime(),
    issuedByName: z.string(),
    receivedByName: z.string(),
    precautions: z.string().nullable(),
    isRevoked: z.boolean(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type Permit = z.infer<typeof PermitSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const PermitStrict = PermitSchema.strict();

/** Compatible mobile list envelope: required fields stay validated while
 * additive server fields are ignored at both envelope and item boundaries. */
export const PermitListResponseSchema = CursorPaginatedResponse(PermitSchema.strip());

export const PermitCreateRequest = z
  .object({
    permitType: PermitTypeSchema,
    location: z.string().min(1),
    description: z.string().min(1),
    workStartsAt: z.string().datetime(),
    workEndsAt: z.string().datetime(),
    issuedByName: z.string().min(1),
    receivedByName: z.string().min(1),
    precautions: z.string().nullable().optional(),
  })
  .strict();

export type PermitCreateRequest = z.infer<typeof PermitCreateRequest>;

export const PermitUpdateRequest = PermitCreateRequest.partial();

export type PermitUpdateRequest = z.infer<typeof PermitUpdateRequest>;

/**
 * POST /api/mobile/v1/projects/:projectId/permits/:permitId/revoke body.
 * Optional reason captured for audit log; the actual revocation is
 * server-driven (sets isRevoked=true, revokedAt=now()).
 */
export const PermitRevokeRequest = z
  .object({
    reason: z.string().min(1).optional(),
  })
  .strict();

export type PermitRevokeRequest = z.infer<typeof PermitRevokeRequest>;
